import { yup } from '@framework/validation/initYup.ts';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { isPlainArray } from '../../../../expressions/evaluate/ExpressionValue.ts';
import { evaluateExpression } from '../../../../expressions/evaluate/evaluateExpression.ts';
import { mapReferenceToPath } from '../../../../expressions/evaluate/mapReferenceToPath.ts';
import { createEvaluationVariables } from '../../../../expressions/evaluate/variables/IEvaluationVariables.ts';
import { collectExpressionReferences } from '../../../../expressions/references/collectExpressionReferences.ts';
import { type IAssignableExpression } from '../../../../expressions/repository/assignable/IAssignableExpression.ts';
import { type ICodeWidget } from '../../../ICodeWidget.ts';
import {
  type IWidgetAdvancedOption,
  type IWidgetOptionValue,
} from '../../../Widget.ts';
import { transformAllNullChildrenToDefault } from './transformAllNullChildrenToDefault.ts';

export interface IMultiSelectSchemaConfig<
  W extends ICodeWidget,
  O extends IWidgetOptionValue | IWidgetAdvancedOption =
    | IWidgetOptionValue
    | IWidgetAdvancedOption,
> {
  widget: W;
  optionsObjectName: string;
  /**
   * Array di opzioni con le loro chiavi
   */
  optionsWithKeys: Array<{
    option: O;
    key: string;
  }>;
  /**
   * Funzione per creare lo schema di una singola opzione
   */
  createOptionSchema: (
    parentName: string,
    widget: W,
    option: O,
  ) => yup.AnySchema;
}

/**
 * Crea lo schema dell'oggetto _options@internal per i widget multi-select.
 * Questa funzione è condivisa tra multi-select normale e advanced multi-select.
 */
export function createMultiSelectObjectSchema<
  W extends ICodeWidget,
  O extends IWidgetOptionValue | IWidgetAdvancedOption =
    | IWidgetOptionValue
    | IWidgetAdvancedOption,
>(config: IMultiSelectSchemaConfig<W, O>) {
  const { widget, optionsObjectName, optionsWithKeys, createOptionSchema } =
    config;

  let optionsObjectSchema = yup
    .object(
      Object.fromEntries(
        optionsWithKeys.map(({ option, key }) => [
          key,
          createOptionSchema(optionsObjectName, widget, option),
        ]),
      ),
    )
    .transform(coerceNumericKeysArrayToObject)
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipValidationRules,
    );

  optionsObjectSchema = setMultiSelectObjectDefault(
    widget,
    optionsWithKeys.map(({ key }) => key),
    optionsObjectSchema,
  ) as typeof optionsObjectSchema;
  optionsObjectSchema = optionsObjectSchema.addChildrenParentDeps().noUnknown();

  return optionsObjectSchema;
}

/**
 * In caso di chiavi delle opzioni solo numeriche, il valore dell'optionObject
 * viene impostato come un array da Yup. Questa transform normalizza il valore
 * ad un oggetto per evitare errori di validazione.
 */
function coerceNumericKeysArrayToObject(value: unknown) {
  if (!Array.isArray(value)) return value;
  return Object.fromEntries(
    value.map((optionValue, i) => [i.toString(), optionValue] as const),
  );
}

/**
 * Imposta il valore di default per l'oggetto _options@internal di un multi-select,
 * ovvero passando da `['A', 'B']` a `{ A: true, B: true, C: false }`.
 */
function setMultiSelectObjectDefault<W extends ICodeWidget>(
  widget: W & { defaultValue?: IAssignableExpression | null },
  optionKeys: string[],
  schema: yup.ObjectSchema<yup.AnyObject>,
) {
  if (widget.defaultValue == null) return schema;

  const expression = widget.defaultValue;

  const references = collectExpressionReferences(expression);
  const paths = references.map(r => mapReferenceToPath(r));

  return schema.parentWhen(paths, (values, schema: yup.AnySchema) => {
    const variables = createEvaluationVariables(references, values);
    const result = evaluateExpression(expression, { variables });

    const defaultValue: Record<string, boolean> = Object.fromEntries(
      optionKeys.map(key => [key, false]),
    );

    if (result != null && Array.isArray(result) && isPlainArray(result)) {
      for (const defaultOption of result) {
        defaultValue[defaultOption] = true;
      }
    }

    return (
      schema
        .default(defaultValue)
        // Fondamentale: se il multi-select è disabilitato, vogliamo che il suo
        //  valore sia `undefined`, non l'oggetto con tutte le opzioni a false/undefined
        // (che è comunque un valore "selezionato" e in fase di `syncComputedValues`
        // sarebbe considerato un campo VALORIZZATO, quindi non applicherebbe il default!
        .transform(transformAllNullChildrenToDefault)
    );
  });
}
